/**
 * Bit flags describing the lifecycle and terminal state of a media input.
 */
export var MediaInputStatusFlag;
(function (MediaInputStatusFlag) {
    /** No known status. */
    MediaInputStatusFlag[MediaInputStatusFlag["None"] = 0] = "None";
    /** The input was included in the media request. */
    MediaInputStatusFlag[MediaInputStatusFlag["Requested"] = 1] = "Requested";
    /** Browser permission has not yet been decided. */
    MediaInputStatusFlag[MediaInputStatusFlag["PermissionPrompt"] = 2] = "PermissionPrompt";
    /** Browser permission was granted. */
    MediaInputStatusFlag[MediaInputStatusFlag["PermissionGranted"] = 4] = "PermissionGranted";
    /** Browser permission was denied. */
    MediaInputStatusFlag[MediaInputStatusFlag["PermissionDenied"] = 8] = "PermissionDenied";
    /** At least one device is available for the input. */
    MediaInputStatusFlag[MediaInputStatusFlag["DeviceAvailable"] = 16] = "DeviceAvailable";
    /** The acquired stream contains a track for the input. */
    MediaInputStatusFlag[MediaInputStatusFlag["TrackPresent"] = 32] = "TrackPresent";
    /** The acquired track exactly matches the requested device. */
    MediaInputStatusFlag[MediaInputStatusFlag["ExactMatch"] = 64] = "ExactMatch";
    /** A fallback device was acquired instead of the requested device. */
    MediaInputStatusFlag[MediaInputStatusFlag["Fallback"] = 128] = "Fallback";
    /** No device is available for the input. */
    MediaInputStatusFlag[MediaInputStatusFlag["NoDevice"] = 256] = "NoDevice";
    /** The specifically requested device was not found. */
    MediaInputStatusFlag[MediaInputStatusFlag["RequestedDeviceNotFound"] = 512] = "RequestedDeviceNotFound";
    /** The device could not start, typically because it is already in use. */
    MediaInputStatusFlag[MediaInputStatusFlag["DeviceInUse"] = 1024] = "DeviceInUse";
    /** The requested constraints cannot be satisfied. */
    MediaInputStatusFlag[MediaInputStatusFlag["Overconstrained"] = 2048] = "Overconstrained";
    /** The supplied constraints are invalid. */
    MediaInputStatusFlag[MediaInputStatusFlag["InvalidConstraints"] = 4096] = "InvalidConstraints";
    /** User-media acquisition is unsupported. */
    MediaInputStatusFlag[MediaInputStatusFlag["NotSupported"] = 8192] = "NotSupported";
    /** User-media acquisition failed for an unknown reason. */
    MediaInputStatusFlag[MediaInputStatusFlag["UnknownError"] = 16384] = "UnknownError";
    /** User-media acquisition exceeded its timeout. */
    MediaInputStatusFlag[MediaInputStatusFlag["TimedOut"] = 32768] = "TimedOut";
})(MediaInputStatusFlag || (MediaInputStatusFlag = {}));
/** Bit masks for extracting status categories from a media input status. */
export const InputStatusMask = {
    Permission: 14,
    Match: 192,
    Error: 65280,
    Known: 65535,
};
const hasAtMostOneBit = (value) => value === 0 || (value & (value - 1)) === 0;
export const isMediaInputStatus = (value) => {
    if (typeof value !== 'number' ||
        !Number.isInteger(value) ||
        value < 0 ||
        value > InputStatusMask.Known ||
        (value & ~InputStatusMask.Known) !== 0) {
        return false;
    }
    const match = value & InputStatusMask.Match;
    const hasTrack = (value & MediaInputStatusFlag.TrackPresent) !== 0;
    const requested = (value & MediaInputStatusFlag.Requested) !== 0;
    return (hasAtMostOneBit(value & InputStatusMask.Permission) &&
        hasAtMostOneBit(value & InputStatusMask.Match) &&
        hasAtMostOneBit(value & InputStatusMask.Error) &&
        (match === 0 || (hasTrack && requested)));
};
const assertMediaInputStatus = (value) => {
    if (!isMediaInputStatus(value)) {
        throw new Error(`Invalid media input status: ${value}`);
    }
};
export const createUserMediaStatus = ({ audio = MediaInputStatusFlag.None, video = MediaInputStatusFlag.None, } = {}) => {
    assertMediaInputStatus(audio);
    assertMediaInputStatus(video);
    return Object.freeze({ audio, video });
};
export const EMPTY_USER_MEDIA_STATUS = createUserMediaStatus();
const MEDIA_INPUT_STATUS_FLAGS = [
    ['Requested', MediaInputStatusFlag.Requested],
    ['PermissionPrompt', MediaInputStatusFlag.PermissionPrompt],
    ['PermissionGranted', MediaInputStatusFlag.PermissionGranted],
    ['PermissionDenied', MediaInputStatusFlag.PermissionDenied],
    ['DeviceAvailable', MediaInputStatusFlag.DeviceAvailable],
    ['TrackPresent', MediaInputStatusFlag.TrackPresent],
    ['ExactMatch', MediaInputStatusFlag.ExactMatch],
    ['Fallback', MediaInputStatusFlag.Fallback],
    ['NoDevice', MediaInputStatusFlag.NoDevice],
    ['RequestedDeviceNotFound', MediaInputStatusFlag.RequestedDeviceNotFound],
    ['DeviceInUse', MediaInputStatusFlag.DeviceInUse],
    ['Overconstrained', MediaInputStatusFlag.Overconstrained],
    ['InvalidConstraints', MediaInputStatusFlag.InvalidConstraints],
    ['NotSupported', MediaInputStatusFlag.NotSupported],
    ['UnknownError', MediaInputStatusFlag.UnknownError],
    ['TimedOut', MediaInputStatusFlag.TimedOut],
];
const formatMediaInputStatus = (status) => {
    const flags = MEDIA_INPUT_STATUS_FLAGS.flatMap(([name, flag]) => (status & flag) === flag ? [name] : []);
    return flags.length ? flags : ['None'];
};
export const formatUserMediaStatus = (status) => ({
    audio: formatMediaInputStatus(status.audio),
    video: formatMediaInputStatus(status.video),
});
export const hasInputStatus = (status, kind, flags) => (status[kind] & flags) === flags;
export const updateInputStatus = (status, kind, value) => {
    assertMediaInputStatus(value);
    if (status[kind] === value) {
        return status;
    }
    return createUserMediaStatus({ ...status, [kind]: value });
};
export const replaceInputStatus = (status, kind, mask, value) => {
    if ((value & ~mask) !== 0) {
        throw new Error(`Status ${value} is outside mask ${mask}`);
    }
    return updateInputStatus(status, kind, (status[kind] & ~mask) | value);
};
export const areUserMediaStatusesEqual = (first, second) => first.audio === second.audio && first.video === second.video;
export var DeniedDevices;
(function (DeniedDevices) {
    DeniedDevices["Microphone"] = "microphone";
    DeniedDevices["Camera"] = "camera";
    DeniedDevices["Both"] = "microphone-and-camera";
})(DeniedDevices || (DeniedDevices = {}));
/**
 * Audio content hints are only applicable when the MediaStreamTrack contains an
 * audio track
 *
 * {@link https://www.w3.org/TR/mst-content-hint/#audio-content-hints}
 */
export const AUDIO_CONTENT_HINTS = {
    /**
     * No hint has been provided, the implementation should make its
     * best-informed guess on how to handle contained audio data. This may be
     * inferred from how the track was opened or by doing content analysis
     */
    NoHint: '',
    /**
     * The track should be treated as if it contains speech data. Consuming this
     * signal it may be appropriate to apply noise suppression or boost
     * intelligibility of the incoming signal.
     */
    Speech: 'speech',
    /**
     * The track should be treated as if it contains data for the purpose of
     * speech recognition by a machine. Consuming this signal it may be
     * appropriate to boost intelligibility of the incoming signal for
     * transcription and turn off audio-processing components that are used for
     * human consumption.
     */
    SpeechRecognition: 'speech-recognition',
    /**
     * The track should be treated as if it contains music data. Generally this
     * might imply tuning or turning off audio-processing components that are
     * used to process speech data to prevent the audio from being distorted.
     */
    Music: 'music',
};
/**
 * Video content hints are only applicable when the MediaStreamTrack contains a
 * video track.
 *
 * {@link https://www.w3.org/TR/mst-content-hint/#video-content-hints}
 */
export const VIDEO_CONTENT_HINTS = {
    /**
     * No hint has been provided, the implementation should make its
     * best-informed guess on how contained video content should be treated.
     * This can for example be inferred from how the track was opened or by
     * doing content analysis.
     */
    NoHint: '',
    /**
     * The track should be treated as if it contains video where motion is
     * important. This is normally webcam video, movies or video games.
     * Quantization artefacts and downscaling are acceptable in order to
     * preserve motion as well as possible while still retaining target
     * bitrates. During low bitrates when compromises have to be made, more
     * effort is spent on preserving frame rate than edge quality and details.
     */
    Motion: 'motion',
    /**
     * The track should be treated as if video details are extra important.
     * This is generally applicable to presentations or web pages with text
     * content, painting or line art. This setting would normally optimize for
     * detail in the resulting individual frames rather than smooth playback.
     * Artefacts from quantization or downscaling that make small text or line
     * art unintelligible should be avoided.
     */
    Detail: 'detail',
    /**
     * The track should be treated as if video details are extra important, and
     * that significant sharp edges and areas of consistent color can occur
     * frequently. This is generally applicable to presentations or web pages
     * with text content. This setting would normally optimize for detail in the
     * resulting individual frames rather than smooth playback, and may take
     * advantage of encoder tools that optimize for text rendering. Artefacts
     * from quantization or downscaling that make small text or line art
     * unintelligible should be avoided.
     */
    Text: 'text',
};
//# sourceMappingURL=types.js.map